// apps/roleforge-app/src/components/layout/AuditWorkspaceMenu.tsx
//
// Bottom-of-sidebar block: workspace label on the left, avatar circle on the
// right. Clicking the avatar opens a profile popout (theme picker + sign out)
// matching the VibeMailer / VibeControl avatar-menu pattern.

import { useEffect, useRef, useState } from "react";
import { Check, ChevronDown, LogOut, Monitor, Sun, Moon } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../lib/auth";
import { useTheme, type ThemePref } from "../../lib/theme";
import {
  getCurrentAudit,
  getWorkspaceBootstrap,
  listAudits,
  type WorkspaceBootstrap,
  type WorkspaceBootstrapWorkspace,
} from "../../lib/api-client";

interface Props {
  workspaceId?: string;
  workspaceName?: string;
}

function profileName(
  bootstrap: WorkspaceBootstrap | null,
  email: string,
): string {
  const profile = bootstrap?.profile;
  return (
    profile?.display_name?.trim() ||
    [profile?.first_name, profile?.last_name]
      .filter(Boolean)
      .join(" ")
      .trim() ||
    email ||
    "Operator"
  );
}

function isFreshBuyerWorkspace(
  bootstrap: WorkspaceBootstrap | null,
  workspace: WorkspaceBootstrapWorkspace | undefined,
  email: string,
): boolean {
  if (!bootstrap || !workspace || bootstrap.organizations.length !== 1)
    return false;
  const org = bootstrap.organizations[0];
  const workspaces = bootstrap.allWorkspacesByOrg[org.id] ?? [];
  if (
    org.plan !== "free" ||
    !org.preactivation_sponsor_id ||
    org.preactivation_activated_at ||
    org.preactivation_expired_at ||
    workspaces.length !== 1
  )
    return false;

  const generatedNames = [
    bootstrap.profile?.display_name,
    bootstrap.profile?.first_name,
    email.split('@')[0],
  ]
    .filter(Boolean)
    .map((name) => `${String(name).trim()}'s Workspace`.toLowerCase());
  return generatedNames.includes(workspace.name.trim().toLowerCase());
}

function ThemePicker() {
  const { pref, setPref } = useTheme();
  const opts: Array<{ value: ThemePref; label: string; Icon: typeof Monitor }> =
    [
      { value: "system", label: "System", Icon: Monitor },
      { value: "light", label: "Light", Icon: Sun },
      { value: "dark", label: "Dark", Icon: Moon },
    ];
  return (
    <div className="px-3 pb-2 pt-2.5">
      <div className="mb-1.5 px-1 text-[10px] font-semibold uppercase tracking-wider text-graphite">
        Theme
      </div>
      <div className="flex gap-1 rounded-lg border border-line bg-paper/40 p-1">
        {opts.map(({ value, label, Icon }) => {
          const active = pref === value;
          return (
            <button
              key={value}
              type="button"
              onClick={() => setPref(value)}
              className={`flex flex-1 items-center justify-center gap-1.5 rounded-md px-2 py-1.5 text-xs font-medium transition-colors ${
                active
                  ? "bg-card text-ink shadow-sm ring-1 ring-line"
                  : "text-graphite hover:text-ink"
              }`}
              aria-pressed={active}
            >
              <Icon className="h-3.5 w-3.5" />
              {label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

interface WorkspaceSwitcherProps {
  jwt: string;
  workspaceId: string;
  displayWorkspace: string;
  workspaceGroups: Array<{
    organization: WorkspaceBootstrap["organizations"][number];
    workspaces: WorkspaceBootstrapWorkspace[];
  }>;
  onOpen: () => void;
}

function WorkspaceSwitcher({
  jwt,
  workspaceId,
  displayWorkspace,
  workspaceGroups,
  onOpen,
}: WorkspaceSwitcherProps) {
  const navigate = useNavigate();
  const [showMenu, setShowMenu] = useState(false);
  const [switchError, setSwitchError] = useState<string | null>(null);
  const [switchingWorkspaceId, setSwitchingWorkspaceId] = useState<
    string | null
  >(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!showMenu) return;
    function handlePointerDown(e: MouseEvent) {
      if (!menuRef.current?.contains(e.target as Node)) setShowMenu(false);
    }
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") {
        setShowMenu(false);
        triggerRef.current?.focus();
      }
    }
    window.addEventListener("mousedown", handlePointerDown);
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("mousedown", handlePointerDown);
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [showMenu]);

  async function switchWorkspace(target: WorkspaceBootstrapWorkspace) {
    if (target.id === workspaceId) {
      setShowMenu(false);
      return;
    }
    setSwitchError(null);
    setSwitchingWorkspaceId(target.id);
    try {
      const current = await getCurrentAudit(jwt, target.id);
      if (current) {
        navigate(`/audits/${current.id}`);
        return;
      }
      const { audits } = await listAudits(jwt, target.id);
      const materialized = audits.find(
        (audit) => audit.status === "materialized",
      );
      navigate(
        materialized
          ? `/audits/${materialized.id}`
          : `/start?workspace=${encodeURIComponent(target.id)}`,
      );
    } catch {
      setSwitchError("Could not open that workspace. Please try again.");
      triggerRef.current?.focus();
    } finally {
      setSwitchingWorkspaceId(null);
      setShowMenu(false);
    }
  }

  return (
    <div className="relative min-w-0 flex-1" ref={menuRef}>
      {showMenu && (
        <div className="absolute bottom-[calc(100%+8px)] left-0 z-50 w-64 overflow-hidden rounded-2xl border border-line bg-surface shadow-[0_20px_48px_rgba(0,0,0,0.45)]">
          {workspaceGroups.map(
            ({ organization, workspaces }) =>
              workspaces.length > 0 && (
                <div
                  key={organization.id}
                  className="border-b border-line last:border-b-0"
                >
                  <div className="px-3 pb-1 pt-3 text-[10px] font-semibold uppercase tracking-wider text-graphite">
                    {organization.name}
                  </div>
                  <div className="px-1.5 pb-1.5">
                    {workspaces.map((workspace) => (
                      <button
                        key={workspace.id}
                        type="button"
                        onClick={() => void switchWorkspace(workspace)}
                        disabled={Boolean(switchingWorkspaceId)}
                        aria-current={
                          workspace.id === workspaceId ? "true" : undefined
                        }
                        className="flex w-full items-center gap-2 rounded-lg px-2 py-2 text-left text-sm text-ink hover:bg-card disabled:opacity-60"
                      >
                        <span className="min-w-0 flex-1 truncate">
                          {workspace.name}
                        </span>
                        {workspace.id === workspaceId && (
                          <Check className="h-4 w-4 text-coral" />
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              ),
          )}
        </div>
      )}
      <button
        ref={triggerRef}
        type="button"
        aria-label={`Switch workspace, ${displayWorkspace}`}
        aria-expanded={showMenu}
        onClick={() => {
          onOpen();
          setShowMenu((open) => !open);
        }}
        className="flex min-h-11 w-full items-center gap-2 rounded-xl border border-line bg-card px-3 py-2 text-left transition-colors hover:bg-card-hover"
      >
        <div className="min-w-0 flex-1">
          <div className="truncate text-[13px] font-semibold text-ink">
            {displayWorkspace}
          </div>
          <div className="truncate text-[11px] text-graphite">
            Switch workspace
          </div>
        </div>
        <ChevronDown className="h-4 w-4 shrink-0 text-graphite" />
      </button>
      {switchError && (
        <div aria-live="polite" className="mt-1 text-[11px] text-coral">
          {switchError}
        </div>
      )}
    </div>
  );
}

export function AuditWorkspaceMenu({ workspaceId, workspaceName }: Props) {
  const { jwt, session, signOut } = useAuth();
  const [bootstrap, setBootstrap] = useState<WorkspaceBootstrap | null>(null);
  const [bootstrapError, setBootstrapError] = useState(false);
  const [bootstrapRequest, setBootstrapRequest] = useState(0);
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const profileMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!jwt || !workspaceId) return;
    let cancelled = false;
    void getWorkspaceBootstrap(jwt)
      .then((value) => {
        if (!cancelled) {
          setBootstrap(value);
          setBootstrapError(false);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setBootstrap(null);
          setBootstrapError(true);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [jwt, workspaceId, bootstrapRequest]);

  useEffect(() => {
    if (!showProfileMenu) return;
    function handlePointerDown(e: MouseEvent) {
      if (!profileMenuRef.current?.contains(e.target as Node)) {
        setShowProfileMenu(false);
      }
    }
    window.addEventListener("mousedown", handlePointerDown);
    return () => window.removeEventListener("mousedown", handlePointerDown);
  }, [showProfileMenu]);

  const email = session?.user?.email ?? "";
  const operatorName = profileName(bootstrap, email);
  const initial = (operatorName[0] ?? "?").toUpperCase();
  const avatarUrl = bootstrap?.profile?.avatar_url;
  const workspaceGroups =
    bootstrap?.organizations.map((organization) => ({
      organization,
      workspaces: bootstrap.allWorkspacesByOrg[organization.id] ?? [],
    })) ?? [];
  const allWorkspaces = workspaceGroups.flatMap((group) => group.workspaces);
  const currentWorkspace = allWorkspaces.find(
    (workspace) => workspace.id === workspaceId,
  );
  const displayWorkspace =
    currentWorkspace?.name ?? workspaceName ?? "Workspace";
  const freshBuyer = isFreshBuyerWorkspace(bootstrap, currentWorkspace, email);
  // Established accounts keep the same workspace-switcher affordance as the
  // other apps even when only one workspace is currently available.
  const canSwitch = !freshBuyer && Boolean(currentWorkspace);

  return (
    <div className="border-t border-line px-3 pb-4 pt-3">
      <div
        className="flex items-center gap-2"
        data-testid="workspace-menu-button"
      >
        {bootstrapError ? (
          <div className="min-w-0 flex-1 rounded-xl border border-line bg-card px-3 py-2">
            <div className="truncate text-[13px] font-semibold text-ink">
              Workspace details unavailable
            </div>
            <button
              type="button"
              onClick={() => setBootstrapRequest((request) => request + 1)}
              className="text-[11px] font-medium text-coral hover:underline"
            >
              Retry
            </button>
          </div>
        ) : canSwitch ? (
          <WorkspaceSwitcher
            jwt={jwt!}
            workspaceId={workspaceId!}
            displayWorkspace={displayWorkspace}
            workspaceGroups={workspaceGroups}
            onOpen={() => setShowProfileMenu(false)}
          />
        ) : (
          <div className="flex min-h-11 items-center gap-3 rounded-xl border border-line bg-card px-3 py-2">
            <div className="min-w-0 flex-1">
              <div className="truncate text-[13px] font-semibold text-ink">
                {freshBuyer ? "Blueprint in progress" : displayWorkspace}
              </div>
              <div className="truncate text-[11px] text-graphite">
                {freshBuyer ? "Workspace name comes later" : email}
              </div>
            </div>
          </div>
        )}

        {/* Avatar button + profile popout */}
        <div className="relative" ref={profileMenuRef}>
          {showProfileMenu && (
            <>
              <button
                type="button"
                aria-label="Close profile menu"
                className="fixed inset-0 z-40 cursor-default bg-transparent"
                onClick={() => setShowProfileMenu(false)}
              />
              <div
                className="absolute bottom-0 left-[calc(100%+12px)] z-50 w-72 overflow-hidden rounded-2xl border border-line bg-surface shadow-[0_20px_48px_rgba(0,0,0,0.45)]"
                data-testid="workspace-menu"
              >
                <div className="flex items-center gap-3 border-b border-line px-4 py-4">
                  <div className="flex h-11 w-11 shrink-0 items-center justify-center overflow-hidden rounded-full bg-coral">
                    {avatarUrl ? (
                      <img
                        src={avatarUrl}
                        alt={operatorName}
                        className="h-full w-full object-cover"
                      />
                    ) : (
                      <span className="text-sm font-semibold text-white">
                        {initial}
                      </span>
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-medium text-ink">
                      {operatorName}
                    </div>
                    {email && operatorName !== email && (
                      <div className="truncate text-xs text-graphite">
                        {email}
                      </div>
                    )}
                  </div>
                </div>

                <ThemePicker />

                <button
                  type="button"
                  onClick={() => {
                    setShowProfileMenu(false);
                    void signOut();
                  }}
                  className="flex w-full items-center gap-3 border-t border-line px-3 py-2.5 text-sm text-graphite transition-colors hover:bg-card hover:text-ink"
                >
                  <LogOut className="h-4 w-4" />
                  Sign Out
                </button>
              </div>
            </>
          )}

          <button
            type="button"
            onClick={() => {
              setShowProfileMenu((o) => !o);
            }}
            className="flex h-11 w-11 items-center justify-center overflow-hidden rounded-full bg-coral transition-transform hover:scale-[1.02]"
            title={email}
            data-testid="profile-menu-button"
          >
            {avatarUrl ? (
              <img
                src={avatarUrl}
                alt={operatorName}
                className="h-full w-full object-cover"
              />
            ) : (
              <span className="text-sm font-semibold text-white">
                {initial}
              </span>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
